// This polyfill has been added to support Opera Mobile and old versions of iOS Safari.
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as Sentry from '@sentry/react';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createStore } from 'redux';
import { Provider } from 'react-redux';
import { createBrowserHistory } from 'history';
import App from './appComponents/App';
import appReducer from './core/appReducer';
import { apiRootUrl, environment, reactAppinsightsInstrumentationKey, sentryDsn } from './config.local';

const isIgnorableError = (event: Sentry.Event) => {
	// When this error occurred which caused by the browser's extension, stop reporting to sentry.
	if (event?.exception?.values[0]?.value === 'ResizeObserver loop limit exceeded') return true;
	if (event?.exception?.values[0]?.value?.includes('ResizeObserver loop completed with undelivered notifications'))
		return true;
	if (event?.exception?.values[0]?.value?.includes('UnhandledRejection')) return true;
	return false;
};
if (sentryDsn !== null) {
	Sentry.init({
		environment,
		dsn: sentryDsn,
		beforeSend: (event) => (isIgnorableError(event) ? null : event)
	});
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
if (environment === 'local' && module && (module as any).hot) (module as any).hot.accept();

const browserHistory = createBrowserHistory();
const reactPlugin = new ReactPlugin();
const appInsights = new ApplicationInsights({
	config: {
		instrumentationKey: reactAppinsightsInstrumentationKey,
		extensions: [reactPlugin],
		extensionConfig: {
			[reactPlugin.identifier]: { history: browserHistory }
		}
	}
});

appInsights.loadAppInsights();

const store = createStore(appReducer);

// load GTM script dynamically and set nonce
const gtmInit = async () => {
	let nonce = '{SERVER-GENERATED-NONCE}';
	nonce = await fetch(`${apiRootUrl}/api/nonce`)
		.then((response) => response.text())
		.then((data) => data);

	// set content security policy and update nonce
	const cspContent = `default-src 'self' *.vetting.com data: blob:; script-src 'self' 'nonce-{SERVER-GENERATED-NONCE}' https://www.googletagmanager.com https://tagmanager.google.com https://www.google-analytics.com https://ssl.google-analytics.com https://maps.googleapis.com js.stripe.com polyfill.io https://sentry.io az416426.vo.msecnd.net https://localhost:*; connect-src https://psasefnapp-phoenix-dev-v6.azurewebsites.net https://dc.services.visualstudio.com *.vetting.com http://localhost:* ws://localhost:* https://sentry.io https://www.google-analytics.com; font-src 'self' data: fonts.googleapis.com https://fonts.gstatic.com use.typekit.net; style-src 'unsafe-inline' https://tagmanager.google.com https://fonts.googleapis.com use.typekit.net p.typekit.net; frame-src www.google.com js.stripe.com https://localhost:*; manifest-src 'none'; media-src 'none'; child-src 'none'; worker-src 'none'; object-src 'none'; img-src 'self' https://ssl.gstatic.com https://www.gstatic.com https://www.google-analytics.com data:;`;
	const metaCsp = document.createElement('meta');
	metaCsp.setAttribute('http-equiv', 'http-equiv');
	metaCsp.setAttribute('content', cspContent.replace('{SERVER-GENERATED-NONCE}', nonce));
	document.head.insertBefore(metaCsp, document.head.childNodes[0]);

	// set google tag manager script
	const gtmScript = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;var n=d.querySelector('[nonce]');
n&&j.setAttribute('nonce',n.nonce||n.getAttribute('nonce'));f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-TVKZX5V');`;

	const script = () => {
		const scriptElm = document.createElement('script');
		scriptElm.setAttribute('nonce', nonce);
		scriptElm.innerHTML = gtmScript;
		return scriptElm;
	};

	document.head.insertBefore(script(), document.head.childNodes[0]);
};

gtmInit();

ReactDOM.render(
	<Provider store={store}>
		<App />
	</Provider>,
	document.getElementById('app')
);
